import { createFileRoute, Outlet } from "@tanstack/react-router"
import { zodValidator } from "@tanstack/zod-adapter"
import {
  type ICollectionGroup,
  useCollectionGroups,
  useGetCollectionsWithDetails,
} from "~/hooks/useCollections"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import {
  DemandPlanningCollectionNotFound,
  ensureCollectionExists,
  parseCollectionIdParams,
} from "~/components/collections/collectionRouteGuard"
import { z } from "zod"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { Module } from "~/components/design-system/pages"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { ManageCollectionDialog } from "~/components/collections/dialogs/ManageCollectionDialog"
import {
  ChartBar,
  ChartLine,
  PencilSimple,
  PresentationChart,
  Scissors,
  Speedometer,
  TrashSimple,
  Waveform,
} from "@phosphor-icons/react"
import RemoveCollectionDialog from "~/components/collections/dialogs/RemoveCollectionDialog"
import { AddVariantsToCollectionDialog } from "~/components/collections/dialogs/AddVariantsToCollectionDialog"
import { CollectionProvider } from "~/context/CollectionProvider"
import { first, max, maxBy, min } from "lodash"
import { useFilteredVariants } from "~/hooks/useVariants"
import { useUser } from "~/providers/userProvider"
import { VariantProvider } from "~/context/VariantProvider"
import { ActionButton } from "~/components/reusable/ActionButton"
import RenameCollectionGroupDialog from "~/components/collections/dialogs/RenameCollectionGroupDialog"
import { useFlags } from "~/features/feature-flags"
import { useSkuStore } from "~/providers/sku-store-provider"
import { DemandPlanVersionSelector } from "~/components/demand-plan/DemandPlanVersionSelector"
import { useSessionStorage } from "usehooks-ts"
import { DemandPlanProvider } from "~/context/DemandPlanProvider"
import { useGetDemandPlan } from "~/hooks/useDemandPlan"
import { joinCollectionIds } from "~/utils/namingUtils"
import { filterDefined } from "~/utils/tsUtils"
import { useMemo } from "react"
import { CollectionSource, CollectionType } from "~/models/collection"
import WrongCollectionRouteError from "~/components/reusable/errors/WrongCollectionRouteError"
import { useRecordRecentlyViewed } from "~/hooks/useRecentlyViewed"

enum CollectionTab {
  overview = "Overview",
  trim = "Trim",
  demand = "Demand",
  splits = "Splits",
  seasonality = "Seasonality",
  analysis = "Analysis",
}

const collectionSearchSchema = z.object({
  isGroup: z.boolean().default(false),
})

const RouteComponent = () => {
  const rawVariants = useFilteredVariants()
  const { permissions } = useUser()
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)
  const { data: rawCollections, isLoading } = useGetCollectionsWithDetails({
    warehouseId: selectedWarehouseId,
    channel: selectedChannel,
  })
  const { data: collectionGroups, isLoading: isCollectionGroupsLoading } = useCollectionGroups(
    selectedWarehouseId,
    selectedChannel,
  )
  const {
    demandPlan: demandPlanFlag,
    demandPlanTrim: demandPlanTrimFlag,
    demandPlanSplits: demandPlanSplitsFlag,
    demandPlanSeasonality: demandPlanSeasonalityFlag,
  } = useFlags()
  const { collectionId } = Route.useParams()
  const { isGroup } = Route.useSearch()

  // Memoize collections and derived values to prevent re-computation on tab changes
  const { collectionGroup, collections, startDate, endDate, isFashion, isNoos } = useMemo(() => {
    let cols: typeof rawCollections = []
    let group: ICollectionGroup | undefined

    if (isGroup) {
      group = collectionGroups?.find((cg) => cg.id === Number(collectionId))
      cols = rawCollections?.filter((c) => c.collectionGroupId === group?.id) ?? []
    } else {
      cols = rawCollections?.filter((c) => c.id === Number(collectionId)) ?? []
    }

    const isNoos = cols.length > 0 && cols.every((c) => c.collectionType === CollectionType.Noos)

    return {
      collectionGroup: group,
      collections: cols,
      startDate: min(cols.map((c) => c.startDate)),
      endDate: max(cols.map((c) => c.endDate)),
      isFashion: cols.every((c) => c.collectionType === CollectionType.Mixed),
      isNoos,
    }
  }, [rawCollections, collectionGroups, collectionId, isGroup])

  const demandPlanKey = `selected-demand-plan-version-${joinCollectionIds(collections.map((c) => `${c.id}`))}`
  const [demandPlanId] = useSessionStorage<string | undefined>(demandPlanKey, undefined)
  const { data: demandPlan } = useGetDemandPlan(demandPlanId!, demandPlanId != undefined)

  // Memoize to prevent DemandPlanProvider from re-computing on every tab change
  const demandPlanContextValue = useMemo(
    () => ({
      sourceCollectionIds: demandPlan?.sourceCollection.map((colId) => parseInt(colId, 10)) ?? [],
      baselineCollectionIds:
        demandPlan?.baselineCollection && demandPlan.baselineCollection.length > 0
          ? demandPlan.baselineCollection.map((colId) => parseInt(colId, 10))
          : null,
      demandPlanId: demandPlan?.id,
      demandPlanName: demandPlan?.name,
      defaultSalesMetrics: demandPlan?.defaultSalesMetrics ?? [],
    }),
    [demandPlan],
  )

  const recentLabel =
    collectionGroup?.name ?? (maxBy(collections, (c) => c.skuCount) ?? first(collections))?.name
  useRecordRecentlyViewed(
    !isNoos && collections.length > 0 && recentLabel
      ? {
          type: "demand-plan",
          route: `/demand-planning/fashion/${collectionId}${isGroup ? "?isGroup=true" : ""}`,
          label: recentLabel,
        }
      : null,
  )

  const demandPlanTabs: HeaderTab[] = demandPlanFlag
    ? [
        ...(demandPlanTrimFlag
          ? [
              {
                label: CollectionTab.trim,
                icon: <Scissors />,
                disabled: demandPlanId === undefined,
                tooltip: demandPlanId === undefined ? "Activate Demand Plan To Access" : undefined,
                path: "/demand-planning/fashion/$collectionId/trim",
              } as HeaderTab,
            ]
          : []),
        {
          label: CollectionTab.demand,
          icon: <ChartLine />,
          disabled: demandPlanId === undefined,
          tooltip: demandPlanId === undefined ? "Activate Demand Plan To Access" : undefined,
          path: "/demand-planning/fashion/$collectionId/demand",
        },
        ...(demandPlanSplitsFlag
          ? [
              {
                label: CollectionTab.splits,
                icon: <ChartBar />,
                disabled: demandPlanId === undefined,
                tooltip: demandPlanId === undefined ? "Activate Demand Plan To Access" : undefined,
                path: "/demand-planning/fashion/$collectionId/splits",
              } as HeaderTab,
            ]
          : []),
        ...(demandPlanSeasonalityFlag
          ? [
              {
                label: CollectionTab.seasonality,
                icon: <Waveform />,
                disabled: demandPlanId === undefined,
                tooltip: demandPlanId === undefined ? "Activate Demand Plan To Access" : undefined,
                path: "/demand-planning/fashion/$collectionId/seasonality",
              } as HeaderTab,
            ]
          : []),
      ]
    : []

  const tabs: HeaderTab[] = [
    {
      label: CollectionTab.overview,
      icon: <Speedometer />,
      path: "/demand-planning/fashion/$collectionId",
    },
    ...(isFashion ? demandPlanTabs : []),
    {
      label: CollectionTab.analysis,
      icon: <PresentationChart />,
      path: "/demand-planning/fashion/$collectionId/analysis",
    },
  ]

  const variantIdSet = new Set(collections.flatMap((c) => c.variantIds))
  const variantsToShow = rawVariants.filter((v) => variantIdSet.has(v.variantId))

  if (isLoading || isCollectionGroupsLoading) {
    return <LoadingOverlay messages="Getting the collection ready..." />
  }

  if (collections.length === 0) {
    return <DemandPlanningCollectionNotFound />
  }

  if (isNoos) {
    return (
      <WrongCollectionRouteError collectionId={collectionId} isGroup={isGroup} targetRoute="noos" />
    )
  }

  const firstCollection = maxBy(collections, (c) => c.skuCount) ?? first(collections)!
  const baselineCollectionIds = filterDefined(collections.map((c) => c.predecessor?.id))
  const hasSkus = firstCollection.skuCount > 0

  const actions = (
    <>
      {collectionGroup ? (
        <RenameCollectionGroupDialog
          groupId={collectionGroup.id}
          currentName={collectionGroup.name}
          customTrigger={
            <ActionButton
              size="sm"
              action={{
                name: "Rename group",
                icon: <PencilSimple />,
              }}
            />
          }
        />
      ) : (
        <ManageCollectionDialog
          collection={firstCollection}
          customTrigger={
            <ActionButton
              size="sm"
              action={{
                name: "Edit collection",
                icon: <PencilSimple />,
              }}
            />
          }
        />
      )}
      <RemoveCollectionDialog
        key={firstCollection.id + "-delete"}
        collectionIds={[firstCollection.id]}
        navigateToList
        trigger={
          <ActionButton
            size="sm"
            disabled={!!collectionGroup}
            action={{
              name: firstCollection.externalId ? "Deactivate collection" : "Delete collection",
              icon: <TrashSimple />,
            }}
          />
        }
      />
      {demandPlanFlag && isFashion && (
        <DemandPlanVersionSelector
          collectionIds={collections.map((c) => c.id)}
          baselineCollectionIds={baselineCollectionIds.length > 0 ? baselineCollectionIds : null}
        />
      )}
      {hasSkus && (
        <AddVariantsToCollectionDialog
          collection={firstCollection}
          disabled={!!collectionGroup}
          initiallyOpen={
            !collectionGroup &&
            firstCollection.skuCount === 0 &&
            permissions.canEdit &&
            firstCollection.source === CollectionSource.Manual
          }
          variantIdsInCollection={firstCollection.variantIds}
        />
      )}
    </>
  )

  return (
    <VariantProvider variants={variantsToShow}>
      <CollectionProvider
        value={{ collections, collectionGroups: collectionGroups ?? [], startDate, endDate }}
      >
        <DemandPlanProvider demandPlanContext={demandPlanContextValue}>
          <Module
            breadcrumb={
              <BreadcrumbWrapper
                items={[
                  { label: "Fashion", href: "/demand-planning/fashion/timeline" },
                  { label: collectionGroup?.name ?? firstCollection.name },
                ]}
              />
            }
            actions={actions}
            tabs={
              hasSkus ? { items: tabs, path: "/demand-planning/fashion/$collectionId" } : undefined
            }
          >
            {hasSkus ? (
              <Outlet />
            ) : (
              <div className="flex flex-col gap-2 items-center justify-center h-full">
                <img
                  src="/no_collections.png"
                  alt="No collection"
                  className="w-[220px] h-[160px]"
                />
                <p className="heading-sm">This collection is empty</p>
                <p className="paragraph-base text-fg-subtle text-center mb-4 w-[220px]">
                  Add some products to get started.
                </p>
                <AddVariantsToCollectionDialog
                  collection={firstCollection}
                  disabled={!!collectionGroup}
                  initiallyOpen={
                    !collectionGroup &&
                    firstCollection.skuCount === 0 &&
                    permissions.canEdit &&
                    firstCollection.source === CollectionSource.Manual
                  }
                  variantIdsInCollection={firstCollection.variantIds}
                />
              </div>
            )}
          </Module>
        </DemandPlanProvider>
      </CollectionProvider>
    </VariantProvider>
  )
}

export const Route = createFileRoute("/_auth/demand-planning/fashion/$collectionId/_layout")({
  params: { parse: parseCollectionIdParams },
  validateSearch: zodValidator(collectionSearchSchema),
  loaderDeps: ({ search }) => ({ isGroup: search.isGroup }),
  loader: ({ params, deps, context }) =>
    ensureCollectionExists(context.trpcUtils, params.collectionId, deps.isGroup),
  pendingComponent: () => <LoadingOverlay messages={["Getting the collection ready..."]} />,
  notFoundComponent: DemandPlanningCollectionNotFound,
  component: RouteComponent,
})
